import * as React from "react";
import { useIcon } from "@salt-ds/core";

import "./status-adornment.css";

/**
 * StatusAdornment — the small glyph a form control shows when it is in a validation state.
 *
 * Distinct from `StatusIndicator` despite the near-identical shape, and the difference is where
 * they live rather than what they look like: an adornment sits INSIDE a field's border and is
 * sized and padded to align with the text baseline there, while an indicator is a standalone
 * status marker. They resolve different icon-registry keys for that reason.
 *
 * ── THERE IS NO `info` ADORNMENT, DELIBERATELY ────────────────────────────────────────────
 *
 * The registry defines Error, Warning and Success only, and the stylesheet colours exactly
 * those three. `info` is not a validation state — a field cannot be "informationally invalid" —
 * so the type stops at three rather than accepting a fourth that would render nothing.
 */
export type AdornmentStatus = "error" | "warning" | "success";

export interface StatusAdornmentProps extends Omit<React.SVGAttributes<SVGElement>, "status"> {
  status: AdornmentStatus;
}

/** Spelled out rather than concatenated, so a renamed registry key fails the build. */
const ICON_KEY = {
  error: "ErrorStatusAdornment",
  warning: "WarningStatusAdornment",
  success: "SuccessStatusAdornment",
} as const satisfies Record<AdornmentStatus, string>;

export const StatusAdornment = React.forwardRef<SVGSVGElement, StatusAdornmentProps>(
  function StatusAdornment({ className, status, ...rest }, ref) {
    const AdornmentComponent = useIcon()[ICON_KEY[status]];
    if (AdornmentComponent === undefined) return null;

    const classes = ["saltStatusAdornment", `saltStatusAdornment-${status}`];
    if (className) classes.push(className);

    return <AdornmentComponent aria-label={status} className={classes.join(" ")} {...rest} ref={ref} />;
  },
);
